import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Sparkle, ChartLine } from '@phosphor-icons/react';
import { useAssessmentForm } from '@/hooks/useAssessmentForm';
import { Z_INDEX_STICKY_HEADER } from '@/lib/constants';
import ProgressIndicator from '@/components/assessment/ProgressIndicator';
import NavigationControls from '@/components/assessment/NavigationControls';
import AssessmentStepBody from '@/components/assessment/AssessmentStepBody';

// Premium consulting-style gradient background.
const PAGE_GRADIENT = 'bg-[linear-gradient(135deg,_#F8FAFC_0%,_#EEF6F8_50%,_#F0FDFB_100%)]';

const DiagnosticBanner = () => (
  <div className="bg-gradient-to-br from-slate-900 via-slate-900 to-teal-900 rounded-2xl px-6 sm:px-8 py-7 sm:py-8 text-white shadow-xl shadow-slate-900/10 mb-6">
    <div className="flex items-center gap-3 mb-2">
      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/10 backdrop-blur flex items-center justify-center">
        <Sparkle size={22} weight="fill" className="text-amber-300" />
      </div>
      <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-teal-300">
        Aurea Digital Solutions · AWSAI Framework
      </div>
    </div>
    <h1 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-black mb-2">
      SMART-Workflow Diagnostic
    </h1>
    <p className="text-slate-200 text-sm sm:text-base max-w-2xl leading-relaxed">
      Assess your workflow stability, AI readiness, and transformation opportunities.
      You will receive an executive report with a SMART-Workflow roadmap tailored to your scores.
    </p>
  </div>
);

const StickyProgressBar = ({ step, totalSteps, progress, completedSteps, stepLabels }) => (
  <div
    style={{ zIndex: Z_INDEX_STICKY_HEADER }}
    className="sticky top-0 -mx-4 sm:mx-0 mb-6 backdrop-blur-xl bg-white/85 border border-slate-200/70 rounded-2xl shadow-sm px-4 sm:px-6 py-4"
  >
    <ProgressIndicator
      step={step}
      totalSteps={totalSteps}
      progress={progress}
      completedSteps={completedSteps}
      stepLabels={stepLabels}
    />
  </div>
);

const TopBar = ({ onBack }) => (
  <div className="flex items-center justify-between mb-4">
    <Button
      variant="ghost"
      onClick={onBack}
      className="text-slate-600 hover:text-slate-900"
      data-testid="back-to-home-button"
    >
      <ArrowLeft size={20} className="mr-2" />
      Back to Home
    </Button>
    <div className="hidden sm:flex items-center gap-2 text-xs text-slate-500">
      <ChartLine size={16} weight="bold" />
      <span className="tracking-wider uppercase font-semibold">Executive Diagnostic</span>
    </div>
  </div>
);

const AssessmentPage = () => {
  const navigate = useNavigate();
  const form = useAssessmentForm();
  const {
    step, totalSteps, progress, loading, stepError, completedSteps, stepLabels,
    goNext, goPrevious, submit,
  } = form;

  return (
    <div className={`min-h-screen ${PAGE_GRADIENT} py-8 sm:py-12`}>
      <div className="max-w-[900px] mx-auto px-4 sm:px-6">
        <TopBar onBack={() => navigate('/')} />
        <DiagnosticBanner />
        <StickyProgressBar
          step={step}
          totalSteps={totalSteps}
          progress={progress}
          completedSteps={completedSteps}
          stepLabels={stepLabels}
        />
        <div
          className="bg-white rounded-[20px] shadow-[0_10px_30px_rgba(0,0,0,0.08)] p-6 sm:p-8 lg:p-10"
          data-testid="assessment-card"
        >
          <AssessmentStepBody step={step} form={form} />
          <NavigationControls
            step={step}
            totalSteps={totalSteps}
            loading={loading}
            stepError={stepError}
            onPrevious={goPrevious}
            onNext={goNext}
            onSubmit={submit}
          />
        </div>
      </div>
    </div>
  );
};

export default AssessmentPage;
